/**
 * Dashboard API hooks — aggregate stats + actionable work queue.
 *
 * Fans out to existing scoped list endpoints in parallel and folds results into
 * a single DashboardStats object (one useQuery / shared cache).
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../store/auth.js';
import type {
  DisciplinaryAction,
  Employee,
  Recognition,
  Review,
  TimeOffRequest,
  TimeOffStatus,
  OnboardingAssignment,
  OffboardingAssignment,
  Paginated,
} from '@shared/types';

export interface DashboardWorkItem {
  id: string;
  kind: 'time_off' | 'review' | 'disciplinary' | 'onboarding' | 'offboarding';
  title: string;
  subtitle: string;
  href: string;
}

export interface UpcomingAnniversary {
  id: string;
  employeeId: string;
  employeeName: string;
  hireDate: string;
  anniversaryDate: string;
  years: number;
  href: string;
}

export interface UpcomingLeave {
  id: string;
  employeeId: string;
  employeeName: string;
  type: string;
  from: string;
  to: string;
  status: TimeOffStatus;
  href: string;
}

export interface DashboardStats {
  employeeCount: number;
  pendingTimeOff: number;
  draftReviews: number;
  unackedDisciplinary: number;
  recentRecognition: Recognition[];
  workQueue: DashboardWorkItem[];
  upcomingAnniversaries: UpcomingAnniversary[];
  upcomingLeave: UpcomingLeave[];
}

export const dashboardKeys = {
  all: ['dashboard'] as const,
  stats: () => [...dashboardKeys.all, 'stats'] as const,
};

const MS_PER_DAY = 86_400_000;
const ANNIVERSARY_WINDOW_DAYS = 30;
const LEAVE_WINDOW_DAYS = 30;

function toDay(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : '';
}

function todayUtcDay(from = new Date()): string {
  return from.toISOString().slice(0, 10);
}

function parseDayParts(day: string): { y: number; m: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

function dayDiff(fromDay: string, toDay: string): number {
  const a = Date.parse(`${fromDay}T00:00:00.000Z`);
  const b = Date.parse(`${toDay}T00:00:00.000Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return Number.POSITIVE_INFINITY;
  return Math.round((b - a) / MS_PER_DAY);
}

/** Next hire-anniversary date (YYYY-MM-DD) on or after `today`, plus years of service. */
function nextAnniversary(hireDateIso: string, today: string): { date: string; years: number } | null {
  const hire = parseDayParts(toDay(hireDateIso));
  const now = parseDayParts(today);
  if (!hire || !now) return null;

  let anniversaryYear = now.y;
  let date = `${anniversaryYear}-${String(hire.m).padStart(2, '0')}-${String(hire.d).padStart(2, '0')}`;
  if (date < today) {
    anniversaryYear += 1;
    date = `${anniversaryYear}-${String(hire.m).padStart(2, '0')}-${String(hire.d).padStart(2, '0')}`;
  }

  const years = anniversaryYear - hire.y;
  if (years < 1) return null;
  return { date, years };
}

function computeUpcomingAnniversaries(employees: Employee[], today: string): UpcomingAnniversary[] {
  const items: UpcomingAnniversary[] = [];
  for (const e of employees) {
    if (e.employmentStatus === 'Terminated' || !e.hireDate) continue;
    const next = nextAnniversary(e.hireDate, today);
    if (!next) continue;
    const days = dayDiff(today, next.date);
    if (days < 0 || days > ANNIVERSARY_WINDOW_DAYS) continue;
    items.push({
      id: `ann-${e.id}-${next.date}`,
      employeeId: e.id,
      employeeName: e.name,
      hireDate: e.hireDate,
      anniversaryDate: next.date,
      years: next.years,
      href: `/employees/${e.id}`,
    });
  }
  return items
    .sort(
      (a, b) =>
        a.anniversaryDate.localeCompare(b.anniversaryDate) ||
        a.employeeName.localeCompare(b.employeeName),
    )
    .slice(0, 8);
}

function computeUpcomingLeave(
  timeOff: TimeOffRequest[],
  employeesById: Map<string, Employee>,
  today: string,
): UpcomingLeave[] {
  const items: UpcomingLeave[] = [];
  for (const t of timeOff) {
    if (t.status !== 'Approved' && t.status !== 'Pending') continue;
    const employee = employeesById.get(t.employeeId);
    if (employee?.employmentStatus === 'Terminated') continue;
    const start = toDay(t.from);
    if (!start) continue;
    const days = dayDiff(today, start);
    if (days < 0 || days > LEAVE_WINDOW_DAYS) continue;
    items.push({
      id: `leave-${t.id}`,
      employeeId: t.employeeId,
      employeeName: employee?.name ?? 'Employee',
      type: t.type,
      from: t.from,
      to: t.to,
      status: t.status,
      href: '/time-off',
    });
  }
  return items
    .sort(
      (a, b) =>
        toDay(a.from).localeCompare(toDay(b.from)) || a.employeeName.localeCompare(b.employeeName),
    )
    .slice(0, 8);
}

/** Fetch aggregated dashboard stats from scoped list endpoints in parallel. */
export function useDashboardStats() {
  const role = useAuthStore((s) => s.user?.role);
  const canManageBoarding = role === 'Admin' || role === 'Manager';
  return useQuery({
    queryKey: [...dashboardKeys.stats(), role ?? 'anon'],
    queryFn: async (): Promise<DashboardStats> => {
      const emptyBoarding: Paginated<OnboardingAssignment | OffboardingAssignment> = {
        items: [],
        nextCursor: null,
      };
      const [employees, timeOff, recognition, reviews, disciplinary, onboarding, offboarding] = await Promise.all([
        api.get<Paginated<Employee>>('/api/employees?limit=1000'),
        api.get<Paginated<TimeOffRequest>>('/api/time-off?limit=1000'),
        api.get<Paginated<Recognition>>('/api/recognition?limit=5'),
        api.get<Paginated<Review>>('/api/reviews?limit=1000'),
        api.get<Paginated<DisciplinaryAction>>('/api/disciplinary?limit=1000'),
        canManageBoarding
          ? api.get<Paginated<OnboardingAssignment>>('/api/onboarding/assignments?limit=1000&status=InProgress')
          : Promise.resolve(emptyBoarding as Paginated<OnboardingAssignment>),
        canManageBoarding
          ? api.get<Paginated<OffboardingAssignment>>('/api/offboarding/assignments?limit=1000&status=InProgress')
          : Promise.resolve(emptyBoarding as Paginated<OffboardingAssignment>),
      ]);


      const pending = timeOff.items.filter((t) => t.status === 'Pending');
      const drafts = reviews.items.filter((r) => r.status === 'Draft');
      const unacked = disciplinary.items.filter((d) => !d.acknowledgedAt);

      const workQueue: DashboardWorkItem[] = [
        ...pending.slice(0, 8).map((t) => ({
          id: `to-${t.id}`,
          kind: 'time_off' as const,
          title: `Time off pending — ${t.type}`,
          subtitle: `${toDay(t.from)} → ${toDay(t.to)}`,
          href: '/time-off',
        })),
        ...drafts.slice(0, 8).map((r) => ({
          id: `rv-${r.id}`,
          kind: 'review' as const,
          title: `Draft review — ${r.type}`,
          subtitle: 'Needs share or completion',
          href: `/reviews/${r.id}`,
        })),
        ...unacked.slice(0, 8).map((d) => ({
          id: `da-${d.id}`,
          kind: 'disciplinary' as const,
          title: `Unacknowledged ${d.severity}`,
          subtitle: toDay(d.date) || 'Awaiting acknowledgment',
          href: '/disciplinary',
        })),
        ...onboarding.items.slice(0, 8).map((o) => ({
          id: `ob-${o.id}`,
          kind: 'onboarding' as const,
          title: `Onboarding — ${o.templateName}`,
          subtitle: `${o.completedCount ?? 0}/${o.totalCount ?? 0} complete`,
          href: '/onboarding',
        })),
        ...offboarding.items.slice(0, 8).map((o) => ({
          id: `of-${o.id}`,
          kind: 'offboarding' as const,
          title: `Offboarding — ${o.templateName}`,
          subtitle: `${o.completedCount ?? 0}/${o.totalCount ?? 0} complete`,
          href: '/offboarding',
        })),
      ].slice(0, 12);

      const today = todayUtcDay();
      const employeesById = new Map(employees.items.map((e) => [e.id, e]));

      return {
        employeeCount: employees.items.length,
        pendingTimeOff: pending.length,
        draftReviews: drafts.length,
        unackedDisciplinary: unacked.length,
        recentRecognition: recognition.items,
        workQueue,
        upcomingAnniversaries: computeUpcomingAnniversaries(employees.items, today),
        upcomingLeave: computeUpcomingLeave(timeOff.items, employeesById, today),
      };
    },
  });
}
